import {
  estEngagement,
  signeDansLeChiffre,
  soldeDocument,
  type Customer,
  type CustomerQuery,
  type Page,
  type Transaction,
} from '@iobiz/shared'
import { dbFor, type LocalRow } from '../db.js'

/**
 * Lecture des clients depuis la réplique locale (§7.4).
 *
 * ⚠️ La recherche locale n'est PAS celle du serveur. Le serveur dispose de
 * `pg_trgm` et d'un index `tsvector` non accentué : il tolère les fautes de
 * frappe. Ici, on normalise et on cherche une sous-chaîne — moins puissant,
 * mais instantané et disponible hors ligne. Le jour où l'écart gênera, la
 * réponse sera un index local dédié, pas un aller-retour réseau.
 */

/** Minuscules sans accents : « Éric » et « eric » doivent se rencontrer. */
function fold(value: unknown): string {
  return String(value ?? '')
    .toLowerCase()
    .normalize('NFD')
    // NFD sépare « é » en « e » + accent combinant ; on retire ensuite le bloc
    // des diacritiques combinants (U+0300–U+036F). « eric » trouve « Éric ».
    .replace(/[̀-ͯ]/g, '')
}

/** Ligne répliquée (colonnes SQL) → objet métier (camelCase) attendu par l'UI. */
export function toCustomer(row: LocalRow): Customer {
  const first = String(row['first_name'] ?? '')
  const last = String(row['last_name'] ?? '')
  const company = String(row['company_name'] ?? '')
  const displayName = company.trim() || [last, first].filter(Boolean).join(' ').trim()

  return {
    id: row.id,
    code: (row['code'] as string) ?? null,
    type: (row['type'] as Customer['type']) ?? 'individual',
    firstName: first || null,
    lastName: last || null,
    companyName: company || null,
    displayName,
    taxId: (row['tax_id'] as string) ?? null,
    birthDate: (row['birth_date'] as string) ?? null,
    email: (row['email'] as string) ?? null,
    phone: (row['phone'] as string) ?? null,
    mobile: (row['mobile'] as string) ?? null,
    contactFirstName: (row['contact_first_name'] as string) ?? null,
    contactLastName: (row['contact_last_name'] as string) ?? null,
    contactRole: (row['contact_role'] as string) ?? null,
    contactPhone: (row['contact_phone'] as string) ?? null,
    contactEmail: (row['contact_email'] as string) ?? null,
    addressLine1: (row['address_line1'] as string) ?? null,
    postalCode: (row['postal_code'] as string) ?? null,
    city: (row['city'] as string) ?? null,
    countryCode: (row['country_code'] as string) ?? null,
    paymentTermsDays: Number(row['payment_terms_days'] ?? 0),
    notes: (row['notes'] as string) ?? null,
    tags: (row['tags'] as string[]) ?? [],
    avatarUrl: (row['avatar_url'] as string) ?? null,
    lastActivityAt: (row['last_activity_at'] as string) ?? null,
    createdAt: String(row['created_at'] ?? ''),
    updatedAt: String(row['updated_at'] ?? ''),
    version: row.version,
  } as Customer
}

const texte = (a: string | null, b: string | null): number =>
  fold(a).localeCompare(fold(b), 'fr')

const SORTS: Record<string, (a: Customer, b: Customer) => number> = {
  displayName: (a, b) => a.displayName.localeCompare(b.displayName, 'fr'),
  '-displayName': (a, b) => b.displayName.localeCompare(a.displayName, 'fr'),
  lastActivityAt: (a, b) => (a.lastActivityAt ?? '').localeCompare(b.lastActivityAt ?? ''),
  '-lastActivityAt': (a, b) => (b.lastActivityAt ?? '').localeCompare(a.lastActivityAt ?? ''),
  phone: (a, b) => texte(a.phone, b.phone),
  '-phone': (a, b) => texte(b.phone, a.phone),
  email: (a, b) => texte(a.email, b.email),
  '-email': (a, b) => texte(b.email, a.email),
  city: (a, b) => texte(a.city, b.city),
  '-city': (a, b) => texte(b.city, a.city),
  // Une fiche sans étiquette se range à la fin dans les deux sens : c'est
  // l'absence qui est l'exception, pas une valeur à classer.
  tags: (a, b) => texte(a.tags[0] ?? null, b.tags[0] ?? null),
  '-tags': (a, b) => texte(b.tags[0] ?? null, a.tags[0] ?? null),
  code: (a, b) => texte(a.code, b.code),
  '-code': (a, b) => texte(b.code, a.code),
  // Chaînes ISO : l'ordre alphabétique EST l'ordre chronologique, aucun
  // « new Date() » n'est nécessaire — et aucun fuseau ne s'en mêle.
  createdAt: (a, b) => a.createdAt.localeCompare(b.createdAt),
  '-createdAt': (a, b) => b.createdAt.localeCompare(a.createdAt),
  totalPurchases: (a, b) => (a.totalPurchases ?? 0) - (b.totalPurchases ?? 0),
  '-totalPurchases': (a, b) => (b.totalPurchases ?? 0) - (a.totalPurchases ?? 0),
}

/**
 * Requête clients TELLE QUE LA LIT LA RÉPLIQUE.
 *
 * Même parti pris que pour le catalogue : cette liste ne passe par aucun
 * réseau, ses tris supplémentaires n'ont donc pas à figurer au contrat serveur.
 */
export interface RequeteClients extends Omit<Partial<CustomerQuery>, 'sort'> {
  sort?: string
  /** Une seule étiquette à la fois : c'est ainsi qu'on s'en sert (« Fidèle »). */
  tag?: string
}

/**
 * Ce que chaque client a réellement acheté, cumulé depuis la réplique.
 *
 * ⚠️ Brouillons, DEVIS et ventes ANNULÉES sont exclus, exactement comme au
 * tableau de bord : un devis n'est pas un achat, et compter les annulations
 * gonflerait le montant d'une somme que la comptabilité ne reconnaît pas.
 */
export async function achatsParClient(storeId: string): Promise<Map<string, number>> {
  const lignes = await dbFor(storeId).transactions.toArray()
  const cumul = new Map<string, number>()
  for (const ligne of lignes) {
    const brut = ligne as unknown as Record<string, unknown>
    const client = brut['customer_id'] as string | null
    if (!client) continue
    // ⚠️ Même règle que le tableau de bord — un devis numéroté est « validé »
    // et passerait le seul filtre sur le statut.
    // ⚠️ Le SIGNE, pas seulement l'appartenance : un avoir rend de l'argent et
    // doit retrancher du cumul, sans quoi rembourser un client le ferait
    // passer pour un meilleur client.
    const signe = signeDansLeChiffre({
      docType: String(brut['doc_type'] ?? 'invoice') as Transaction['docType'],
      status: String(brut['status'] ?? 'draft') as Transaction['status'],
    })
    if (signe === 0) continue
    cumul.set(client, (cumul.get(client) ?? 0) + signe * Number(brut['total_amount'] ?? 0))
  }
  return cumul
}

/**
 * Ce que chaque client DOIT — ou ce qu'on lui doit — cumulé sur ses documents.
 *
 * ⭐ Signé : positif, le client doit à la boutique ; négatif, la boutique doit
 * au client. Ce second cas naît au comptoir, quand le tiroir n'a pas la
 * monnaie : on encaisse le billet entier et l'on reste devoir la différence.
 * Il ne se voyait nulle part et se réglait de mémoire.
 *
 * ⚠️ Devis et COMMANDES exclus : ce sont des engagements, rien n'y est dû tant
 * qu'ils ne sont pas facturés. Les compter ferait apparaître en créance le
 * total d'un devis que le client n'a même pas accepté.
 *
 * ⚠️ Annulés exclus aussi : une vente annulée ne réclame plus rien.
 */
export async function soldesParClient(storeId: string): Promise<Map<string, number>> {
  const lignes = await dbFor(storeId).transactions.toArray()
  const soldes = new Map<string, number>()
  for (const ligne of lignes) {
    const brut = ligne as unknown as Record<string, unknown>
    const client = brut['customer_id'] as string | null
    if (!client) continue
    const type = String(brut['doc_type'] ?? 'invoice') as Transaction['docType']
    const statut = String(brut['status'] ?? 'draft') as Transaction['status']
    if (estEngagement(type) || statut === 'cancelled' || statut === 'draft') continue
    const solde = soldeDocument({
      totalAmount: Number(brut['total_amount'] ?? 0),
      paidAmount: Number(brut['paid_amount'] ?? 0),
    })
    if (solde === 0) continue
    soldes.set(client, (soldes.get(client) ?? 0) + solde)
  }
  return soldes
}

export async function searchLocal(
  storeId: string,
  query: RequeteClients,
): Promise<Page<Customer>> {
  const [rows, achats, soldes] = await Promise.all([
    dbFor(storeId).customers.toArray(),
    achatsParClient(storeId),
    soldesParClient(storeId),
  ])
  let list = rows.map((r) => {
    const c = toCustomer(r)
    return { ...c, totalPurchases: achats.get(c.id) ?? 0, solde: soldes.get(c.id) ?? 0 }
  })

  if (query.type) list = list.filter((c) => c.type === query.type)
  if (query.city) list = list.filter((c) => c.city === query.city)
  if (query.tag) list = list.filter((c) => c.tags.includes(query.tag!))

  if (query.q?.trim()) {
    const needle = fold(query.q)
    list = list.filter((c) =>
      [
        c.displayName,
        c.email,
        c.phone,
        c.mobile,
        c.city,
        c.code,
        // ⚠️ L'interlocuteur AUSSI, sans quoi « Aline » trouverait la société
        // en ligne et ne la trouverait plus hors ligne : deux recherches qui
        // ne répondent pas la même chose sont pires qu'une seule imparfaite.
        c.contactFirstName,
        c.contactLastName,
        c.contactPhone,
        c.contactEmail,
        ...c.tags,
      ].some((f) =>
        fold(f).includes(needle),
      ),
    )
  }

  list.sort(SORTS[query.sort ?? 'displayName'] ?? SORTS['displayName']!)

  const offset = query.offset ?? 0
  const limit = query.limit ?? 50
  return { data: list.slice(offset, offset + limit), total: list.length, limit, offset }
}

export async function getLocal(storeId: string, id: string): Promise<Customer | null> {
  const row = await dbFor(storeId).customers.get(id)
  return row ? toCustomer(row) : null
}

/** Étiquettes réellement employées, pour proposer l'existant plutôt qu'une saisie libre. */
export async function etiquettesLocales(storeId: string): Promise<string[]> {
  const rows = await dbFor(storeId).customers.toArray()
  const set = new Set<string>()
  for (const r of rows) for (const t of (r['tags'] as string[]) ?? []) if (t.trim()) set.add(t)
  return [...set].sort((a, b) => a.localeCompare(b, 'fr'))
}

export async function citiesLocal(storeId: string): Promise<string[]> {
  const rows = await dbFor(storeId).customers.toArray()
  const set = new Set<string>()
  for (const r of rows) {
    const city = (r['city'] as string) ?? ''
    if (city.trim()) set.add(city)
  }
  return [...set].sort((a, b) => a.localeCompare(b, 'fr'))
}

/**
 * Écriture immédiate dans la réplique après une écriture serveur réussie.
 *
 * Sans cela, la liste n'afficherait le nouveau client qu'au prochain cycle de
 * synchronisation — jusqu'à trente secondes d'attente après avoir cliqué
 * « Créer ». Le delta suivant confirmera, ou corrigera.
 */
export async function upsertLocal(storeId: string, customer: Customer): Promise<void> {
  await dbFor(storeId).customers.put({
    id: customer.id,
    code: customer.code,
    type: customer.type,
    first_name: customer.firstName,
    last_name: customer.lastName,
    company_name: customer.companyName,
    tax_id: customer.taxId,
    birth_date: customer.birthDate,
    email: customer.email,
    phone: customer.phone,
    mobile: customer.mobile,
    // ⚠️ Noms de colonnes SQL, comme les voisins : la réplique reçoit les
    // deltas du serveur TELS QUELS. Écrire ici en camelCase produisait une
    // ligne à deux vocabulaires — la fiche lisait « contact_last_name » et ne
    // trouvait que « contactLastName », donc rien.
    contact_first_name: customer.contactFirstName,
    contact_last_name: customer.contactLastName,
    contact_role: customer.contactRole,
    contact_phone: customer.contactPhone,
    contact_email: customer.contactEmail,
    address_line1: customer.addressLine1,
    postal_code: customer.postalCode,
    city: customer.city,
    country_code: customer.countryCode,
    payment_terms_days: customer.paymentTermsDays,
    notes: customer.notes,
    tags: customer.tags,
    avatar_url: customer.avatarUrl,
    last_activity_at: customer.lastActivityAt,
    created_at: customer.createdAt,
    updated_at: customer.updatedAt,
    version: customer.version,
    _dirty: 0,
  })
}

export async function removeLocal(storeId: string, id: string): Promise<void> {
  await dbFor(storeId).customers.delete(id)
}

/**
 * Le jour CIVIL de création de chaque fiche — pour le graphe des nouveaux
 * clients.
 *
 * ⚠️ Converti dans le fuseau du POSTE, jamais par `toISOString()` : une fiche
 * créée à 0 h 30 à Douala porte un horodatage UTC de la veille, et tomberait
 * dans la mauvaise colonne.
 */
export async function joursDeCreationClients(storeId: string): Promise<string[]> {
  const lignes = await dbFor(storeId).customers.toArray()
  const deux = (n: number): string => String(n).padStart(2, '0')
  return lignes.flatMap((r) => {
    const instant = new Date(String(r['created_at'] ?? ''))
    if (Number.isNaN(instant.getTime())) return []
    return [`${instant.getFullYear()}-${deux(instant.getMonth() + 1)}-${deux(instant.getDate())}`]
  })
}
